import React, { useMemo, useRef, useState, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

const RAW = 'AF Afghanistan 93|AL Albania 355|DZ Algeria 213|AD Andorra 376|AO Angola 244|AR Argentina 54|AM Armenia 374|AU Australia 61|AT Austria 43|AZ Azerbaijan 994|BH Bahrain 973|BD Bangladesh 880|BY Belarus 375|BE Belgium 32|BZ Belize 501|BJ Benin 229|BT Bhutan 975|BO Bolivia 591|BA Bosnia and Herzegovina 387|BW Botswana 267|BR Brazil 55|BN Brunei 673|BG Bulgaria 359|BF Burkina Faso 226|BI Burundi 257|KH Cambodia 855|CM Cameroon 237|CA Canada 1|CV Cape Verde 238|TD Chad 235|CL Chile 56|CN China 86|CO Colombia 57|KM Comoros 269|CG Congo 242|CD Congo (DRC) 243|CR Costa Rica 506|CI Cote d\u2019Ivoire 225|HR Croatia 385|CU Cuba 53|CY Cyprus 357|CZ Czechia 420|DK Denmark 45|DJ Djibouti 253|DO Dominican Republic 1|EC Ecuador 593|EG Egypt 20|SV El Salvador 503|EE Estonia 372|ET Ethiopia 251|FJ Fiji 679|FI Finland 358|FR France 33|GA Gabon 241|GM Gambia 220|GE Georgia 995|DE Germany 49|GH Ghana 233|GR Greece 30|GT Guatemala 502|GN Guinea 224|GY Guyana 592|HT Haiti 509|HN Honduras 504|HK Hong Kong 852|HU Hungary 36|IS Iceland 354|IN India 91|ID Indonesia 62|IR Iran 98|IQ Iraq 964|IE Ireland 353|IL Israel 972|IT Italy 39|JM Jamaica 1|JP Japan 81|JO Jordan 962|KZ Kazakhstan 7|KE Kenya 254|KW Kuwait 965|KG Kyrgyzstan 996|LA Laos 856|LV Latvia 371|LB Lebanon 961|LS Lesotho 266|LR Liberia 231|LY Libya 218|LI Liechtenstein 423|LT Lithuania 370|LU Luxembourg 352|MO Macau 853|MG Madagascar 261|MW Malawi 265|MY Malaysia 60|MV Maldives 960|ML Mali 223|MT Malta 356|MR Mauritania 222|MU Mauritius 230|MX Mexico 52|MD Moldova 373|MC Monaco 377|MN Mongolia 976|ME Montenegro 382|MA Morocco 212|MZ Mozambique 258|MM Myanmar 95|NA Namibia 264|NP Nepal 977|NL Netherlands 31|NZ New Zealand 64|NI Nicaragua 505|NE Niger 227|NG Nigeria 234|KP North Korea 850|MK North Macedonia 389|NO Norway 47|OM Oman 968|PK Pakistan 92|PS Palestine 970|PA Panama 507|PG Papua New Guinea 675|PY Paraguay 595|PE Peru 51|PH Philippines 63|PL Poland 48|PT Portugal 351|PR Puerto Rico 1|QA Qatar 974|RO Romania 40|RU Russia 7|RW Rwanda 250|SA Saudi Arabia 966|SN Senegal 221|RS Serbia 381|SC Seychelles 248|SL Sierra Leone 232|SG Singapore 65|SK Slovakia 421|SI Slovenia 386|SO Somalia 252|ZA South Africa 27|KR South Korea 82|SS South Sudan 211|ES Spain 34|LK Sri Lanka 94|SD Sudan 249|SR Suriname 597|SE Sweden 46|CH Switzerland 41|SY Syria 963|TW Taiwan 886|TJ Tajikistan 992|TZ Tanzania 255|TH Thailand 66|TG Togo 228|TT Trinidad and Tobago 1|TN Tunisia 216|TR Turkiye 90|TM Turkmenistan 993|UG Uganda 256|UA Ukraine 380|AE United Arab Emirates 971|GB United Kingdom 44|US United States 1|UY Uruguay 598|UZ Uzbekistan 998|VE Venezuela 58|VN Vietnam 84|YE Yemen 967|ZM Zambia 260|ZW Zimbabwe 263';

const flag = (iso) => String.fromCodePoint(...[...iso].map((c) => 127397 + c.charCodeAt(0)));

export const COUNTRIES = RAW.split('|').map((row) => {
  const parts = row.split(' ');
  const iso = parts[0];
  const dial = parts[parts.length - 1];
  const name = parts.slice(1, -1).join(' ');
  return { iso, name, dial, flag: flag(iso) };
});

export const PhoneInput = ({ country, onCountry, value, onChange, placeholder, invalid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(s) || c.dial.includes(s.replace('+', '')) || c.iso.toLowerCase() === s);
  }, [q]);

  const sel = COUNTRIES.find((c) => c.iso === country) || COUNTRIES.find((c) => c.iso === 'GB');

  return (
    <div className="relative" ref={ref}>
      <div className={`flex items-end border-b ${invalid ? 'border-destructive' : 'border-espresso/20'} focus-within:border-gold transition-colors`}>
        <button
          type="button"
          data-testid="phone-country-selector"
          onClick={() => { setOpen((o) => !o); setQ(''); }}
          className="flex items-center gap-1.5 py-3 pr-3 text-espresso shrink-0 hover:text-gold transition-colors"
        >
          <span className="text-lg leading-none">{sel.flag}</span>
          <span className="text-sm">+{sel.dial}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-mocha transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <input
          type="tel"
          inputMode="numeric"
          data-testid="phone-number-input"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d\s-]/g, ''))}
          placeholder={placeholder}
          className="w-full bg-transparent py-3 text-espresso placeholder-mocha/50 focus:outline-none"
        />
      </div>
      {open && (
        <div data-testid="phone-country-dropdown" className="absolute z-30 top-full left-0 mt-1 w-72 max-h-72 overflow-hidden bg-ivory border border-espresso/15 shadow-xl flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-espresso/10 bg-cream">
            <Search className="w-3.5 h-3.5 text-mocha shrink-0" />
            <input
              ref={searchRef}
              data-testid="phone-country-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or code…"
              className="w-full bg-transparent text-sm text-espresso placeholder-mocha/50 focus:outline-none"
            />
          </div>
          <div className="overflow-y-auto">
            {list.length === 0 && <p className="px-4 py-3 text-sm text-mocha">No results</p>}
            {list.map((c) => (
              <button
                key={c.iso}
                type="button"
                data-testid={`phone-country-option-${c.iso}`}
                onClick={() => { onCountry(c.iso); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-cream transition-colors ${c.iso === sel.iso ? 'bg-cream text-gold' : 'text-espresso'}`}
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-mocha">+{c.dial}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const validatePhone = (digitsOnly) => digitsOnly.length >= 6 && digitsOnly.length <= 14;
